import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RecipientRequestService, MsignResponse } from '../../communication/index';

/**
 * Mobile operator information for mobile signing
 */
export interface MobileOperator {
  value: string;
  display: string;
}

/**
 * Parameters for initiating a mobile signature
 */
export interface MobileSignParams {
  requestCode: string;
  operator: string;
  mobile: string;
  reason: string;
  location: string;
}

/**
 * Result of mobile signing initiation
 */
export interface MobileSignResult {
  success: boolean;
  fingerPrint?: string;
  formattedFingerPrint?: string;
  document?: any;
  error?: string;
  documentId?: any;
  isExpectInitialSigning?: boolean;
}

/**
 * Service for handling mobile signature operations.
 *
 * Mobile signing allows users to sign documents using their mobile phone
 * through mobile operator services (Turkcell, Turk Telekom, Vodafone).
 *
 * Flow:
 * 1. User enters mobile number and selects operator
 * 2. System initiates signing and returns OTP fingerprint
 * 3. User confirms signing on their mobile phone
 * 4. System polls for completion status
 */
@Injectable({
  providedIn: 'root'
})
export class MobileSigningService {

  /** Available mobile operators for mobile signing */
  readonly operators: MobileOperator[] = [
    { value: 'turkcell', display: 'Turkcell' },
    { value: 'telekom', display: 'Turk Telekom' },
    { value: 'vodafone', display: 'Vodafone' }
  ];

  constructor(private recipientRequestApi: RecipientRequestService) {}

  /**
   * Get the list of available mobile operators
   */
  getOperators(): MobileOperator[] {
    return [...this.operators];
  }

  /**
   * Get the default operator value
   */
  getDefaultOperator(): string {
    return this.operators[0].value;
  }

  /**
   * Initiate a mobile signature request.
   *
   * IMPORTANT: Parameter order is (code, operator, mobile, reason, location)
   * This fixes the bug in the CAdES component which had incorrect parameter order.
   *
   * @param params Mobile signing parameters
   * @returns Observable with the signing result including formatted fingerprint
   */
  initiateMobileSign(params: MobileSignParams): Observable<MobileSignResult> {
    return this.recipientRequestApi.recipientRequestsCodeMsignPost(
      params.requestCode,
      params.operator,
      params.mobile,
      params.reason,
      params.location
    ).pipe(
      map((response: MsignResponse) => {
        if (response.fingerPrint != null) {
          return {
            success: true,
            fingerPrint: response.fingerPrint,
            formattedFingerPrint: this.formatFingerprint(response.fingerPrint),
            documentId: response.documentId,
            isExpectInitialSigning: response.isExpectInitialSigning
          };
        } else {
          return {
            success: false,
            error: 'No fingerprint returned from mobile signing service'
          };
        }
      })
    );
  }

  /**
   * Format the OTP fingerprint for display.
   * Formats as groups of 4 characters separated by spaces.
   *
   * Example: "1234567890123456" -> "1234 5678 9012 3456"
   *
   * @param fingerPrint Raw fingerprint string
   * @returns Formatted fingerprint string
   */
  formatFingerprint(fingerPrint: string): string {
    if (!fingerPrint) {
      return '';
    }
    // Remove non-alphanumeric characters, then add space every 4 characters
    return fingerPrint
      .replace(/[^\dA-Z]/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim();
  }

  /**
   * Create default form data for mobile signing
   */
  createDefaultFormData(): { mobile: string; operator: string } {
    return {
      mobile: '',
      operator: this.getDefaultOperator()
    };
  }

  /**
   * Validate mobile number format
   * @param mobile Mobile phone number
   * @returns true if valid format
   */
  validateMobileNumber(mobile: string): boolean {
    if (!mobile) {
      return false;
    }
    // Remove spaces and dashes
    const cleaned = mobile.replace(/[\s-]/g, '');
    // Turkish mobile numbers: 10 digits starting with 5
    const turkishMobileRegex = /^5\d{9}$/;
    return turkishMobileRegex.test(cleaned);
  }
}
